import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from 'date-fns'; // Using date-fns for more robust formatting
import ActionMenu from '@/components/ActionMenu';
import ConfirmModal from '@/components/ConfirmModal';
import { CheckCircle, XCircle, Edit, QrCode, ClipboardList, FileText, Trash2, AlertCircle, Copy } from '@/lib/icons';
import FacilitatorLayout from '@/components/FacilitatorLayout';
import { Header } from '@/components/prosci/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";


const FacilitatorAssessmentSessionsIndex = ({ assessmentSessions, newSessionUrl, currentUser }) => {
  const [sessions, setSessions] = useState(assessmentSessions);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, session: null });
  const [clearModal, setClearModal] = useState({ open: false, session: null });
  const [clearError, setClearError] = useState(null);

  // Updated formatDate using date-fns
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // New function to format date only (no time)
  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Parse YYYY-MM-DD format to avoid timezone issues
      const [year, month, day] = dateString.split('T')[0].split('-').map(Number)
      const localDate = new Date(year, month - 1, day)
      return format(localDate, 'MMM d, yyyy')
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const handleDelete = async (session) => {
    setDeleteError(null);
    try {
      const res = await fetch(`/facilitator/assessment_sessions/${session.id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
      })
      if (res.ok) {
        setSessions((prev) => prev.filter((s) => s.id !== session.id));
        setDeleteModal({ open: false, session: null });
      } else {
        const errorData = await res.json().catch(() => null)
        setDeleteError(errorData?.error || 'Failed to delete session.')
      }
    } catch (err) {
      setDeleteError('Failed to delete session.');
    }
  };

  const handleClearResponses = async (session) => {
    setClearError(null);
    try {
      const res = await fetch(`/facilitator/assessment_sessions/${session.id}/clear_responses`, {
        method: 'POST',
        headers: { 
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        },
      });
      if (res.ok) {
        setClearModal({ open: false, session: null });
        // Show success message or refresh data
        window.location.reload();
      } else {
        setClearError('Failed to clear responses.');
      }
    } catch (err) {
      setClearError('Failed to clear responses.');
    }
  };

  const handleCopyLink = (session) => {
    const link = `${window.location.origin}/?code=${session.accessCode}`
    navigator.clipboard
      .writeText(link)
      .then(() => {
        // You could add a toast notification here
        alert('Assessment link copied to clipboard!')
      })
      .catch(() => {
        alert('Failed to copy link')
      })
  }

  // Define menu options for ActionMenu
  const menuOptions = [
    { label: 'Edit', action: 'edit', icon: <Edit className="h-4 w-4" /> },
    { label: 'Copy Link', action: 'copy', icon: <Copy className="h-4 w-4" /> },
    { label: 'Generate QR Code', action: 'qr', icon: <QrCode className="h-4 w-4" /> },
    { label: 'Actions to Ensure Change Success', action: 'view', icon: <ClipboardList className="h-4 w-4" /> },
    { label: 'View Report', action: 'report', icon: <FileText className="h-4 w-4" /> },
    { label: 'Clear All Responses', action: 'clear', destructive: true, icon: <AlertCircle className="h-4 w-4" /> },
    { label: 'Delete', action: 'delete', destructive: true, icon: <Trash2 className="h-4 w-4" /> },
  ]

  return (
    <FacilitatorLayout userName={currentUser?.name || 'Facilitator'}>
      <Header title="Assessment Sessions" size="small">
        <Button asChild variant="lilac">
          <a href={newSessionUrl}>+ Create New Session</a>
        </Button>
      </Header>

      <div className="container mx-auto p-4 md:p-6">
        {deleteError && <p className="text-destructive mb-4">{deleteError}</p>}
        {clearError && <p className="text-destructive mb-4">{clearError}</p>}

        {/* Delete confirmation modal */}
        <ConfirmModal open={deleteModal.open} title="Delete Assessment Session" message="Are you sure you want to delete this assessment session? This action cannot be undone, and all participant responses will be lost." onCancel={() => setDeleteModal({ open: false, session: null })} onConfirm={() => handleDelete(deleteModal.session)} confirmLabel="Delete Session" />

        {/* Clear responses confirmation modal */}
        <ConfirmModal open={clearModal.open} title="Clear All Responses" message="Are you sure you want to clear all participant responses for this assessment session? This action cannot be undone. The session itself will remain active." onCancel={() => setClearModal({ open: false, session: null })} onConfirm={() => handleClearResponses(clearModal.session)} confirmLabel="Clear All Responses" />

        {sessions.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>All Sessions</CardTitle>
              <CardDescription>
                Click a row to view the report for that session.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Session Name</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-center">Access Code</TableHead>
                      <TableHead className="text-center">Submissions</TableHead>
                      <TableHead className="text-center">Active</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((session) => (
                      <TableRow
                        key={session.id}
                        className="cursor-pointer"
                        onClick={(e) => {
                          // Don't navigate if clicking on the actions menu area
                          const isActionMenu = e.target.closest('[data-action-menu]') || e.target.closest('[role="menu"]') || e.target.closest('.text-right') || e.target.closest('a') // Don't navigate if clicking on a link
                          if (!isActionMenu) {
                            window.location.href = `/facilitator/assessment_sessions/${session.id}/report`
                          }
                        }}
                      >
                        <TableCell className="font-medium">
                          <a href={`/facilitator/assessment_sessions/${session.id}`} className="hover:underline text-foreground">
                            {session.name || '-'}
                          </a>
                        </TableCell>
                        {/* Use camelCase for props from Rails */}
                        <TableCell>{session.projectName || 'N/A'}</TableCell>
                        <TableCell>
                          {session.assessmentType === 'full_detailed' && 'Detailed'}
                          {session.assessmentType === 'quick_interactive' && 'Initial'}
                          {session.assessmentType !== 'full_detailed' && session.assessmentType !== 'quick_interactive' && session.assessmentType}
                        </TableCell>
                        <TableCell className="font-mono text-center">{session.accessCode}</TableCell>
                        <TableCell className="text-center">{session.submissionCount || 0}</TableCell>
                        <TableCell className="text-center">
                          {session.active ? (
                            <span className="inline-flex items-center gap-1">
                              <CheckCircle className="text-success w-5 h-5" aria-label="Active" />
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <XCircle className="text-destructive w-5 h-5" aria-label="Inactive" />
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{formatDateOnly(session.expiresAt)}</TableCell>
                        <TableCell>{formatDateOnly(session.createdAt)}</TableCell>
                        <TableCell className="text-right relative z-10">
                          <ActionMenu
                            menuOptions={menuOptions}
                            onSelect={async (action) => {
                              if (action === 'view') {
                                window.location.href = `/facilitator/assessment_sessions/${session.id}`
                              } else if (action === 'edit') {
                                window.location.href = `/facilitator/assessment_sessions/${session.id}/edit`
                              } else if (action === 'copy') {
                                handleCopyLink(session);
                              } else if (action === 'report') {
                                window.location.href = `/facilitator/assessment_sessions/${session.id}/report`
                              } else if (action === 'qr') {
                                window.open(`/facilitator/assessment_sessions/${session.id}/qr`, '_blank', 'noopener,noreferrer')
                              } else if (action === 'clear') {
                                setClearModal({ open: true, session })
                              } else if (action === 'delete') {
                                setDeleteModal({ open: true, session })
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <p className="text-muted-foreground mt-4">No assessment sessions created yet.</p>
        )}
      </div>
    </FacilitatorLayout>
  )
};

FacilitatorAssessmentSessionsIndex.propTypes = {
  assessmentSessions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
      projectId: PropTypes.number,
      projectName: PropTypes.string,
      assessmentType: PropTypes.string.isRequired,
      accessCode: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired,
      expiresAt: PropTypes.string,
      createdAt: PropTypes.string.isRequired,
      submissionCount: PropTypes.number
    })
  ).isRequired,
  newSessionUrl: PropTypes.string.isRequired,
  currentUser: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
  }),
}

export default FacilitatorAssessmentSessionsIndex; 